/**
 * Sequences domain — cache invalidation registry.
 *
 * Every wrapped sequence mutation declares which queries it invalidates on
 * success. Consumed by `hooks.ts` via `useInvalidatingMutation`. The compiler
 * enforces that every member of `SequencesMutationId` has a registry entry, so
 * a new mutation can't ship without declaring its invalidations.
 *
 * Invalidations are campaign-scoped, so each rule reads the lonesome
 * `campaign_id` from the mutation variables (never from the response — the
 * response `campaign_id` is nullable). Mutations that change whether a sequence
 * exists or its step count (`create`/`update`/`delete`) also invalidate the
 * campaign settings cache, which reads sequence/launch-readiness state.
 */

import type { QueryKey } from '@tanstack/react-query'

import { getCampaignSettingsQueryKey } from '../campaigns'
import { type InvalidationRule } from '../invalidation/useInvalidatingMutation'
import { sequenceByCampaignKey } from './utils'

// ── Mutation identifiers ────────────────────────────────────────────

export type SequencesMutationId =
  | 'createSequence'
  | 'updateSequence'
  | 'deleteSequence'
  | 'setSequenceStepActive'
  | 'sendSequenceStepTestEmail'

// ── Shared invalidation sets ────────────────────────────────────────

/** The per-campaign sequence read cache only. */
function sequenceReadInvalidations(campaignId: number): QueryKey[] {
  return [sequenceByCampaignKey(campaignId)]
}

/**
 * The sequence read cache plus the campaign settings cache — for mutations
 * that change sequence existence or step count.
 */
function sequenceAndSettingsInvalidations(campaignId: number): QueryKey[] {
  return [
    sequenceByCampaignKey(campaignId),
    getCampaignSettingsQueryKey({ campaign_id: campaignId }),
  ]
}

// ── Registry ────────────────────────────────────────────────────────

export const invalidationRegistry: Record<
  SequencesMutationId,
  InvalidationRule<any>
> = {
  createSequence: {
    invalidates: (variables: { data: { campaign_id: number } }) =>
      sequenceAndSettingsInvalidations(variables.data.campaign_id),
  },
  updateSequence: {
    invalidates: (variables: { campaign_id: number }) =>
      sequenceAndSettingsInvalidations(variables.campaign_id),
  },
  deleteSequence: {
    invalidates: (variables: { campaign_id: number }) =>
      sequenceAndSettingsInvalidations(variables.campaign_id),
  },
  setSequenceStepActive: {
    // Toggling a variant's active flag changes the sequence display only, not
    // step count / launch readiness — so the settings cache is left alone.
    invalidates: (variables: { campaign_id: number }) =>
      sequenceReadInvalidations(variables.campaign_id),
  },
  sendSequenceStepTestEmail: {
    // Fire-and-forget send; nothing cached changes.
    invalidates: () => [],
  },
}
